/**
 * Тема Mantine поверх тех же токенов, что и Tailwind: компоненты Mantine
 * и утилиты Tailwind выглядят как одна система.
 */

import { createTheme, type MantineColorsTuple } from '@mantine/core';

import { fonts, glass, palette, radii, shadows } from './tokens';

/** Песочный акцент: индекс 5 — основной, 6 — hover, 8 — тёмный текст. */
/** Фиолетовая шкала главного акцента (5 — `palette.accent`). */
const brand: MantineColorsTuple = [
  '#F2F0FF',
  '#E4E0FF',
  '#C9C1FF',
  '#AEA2FF',
  '#9585F8',
  palette.accent,
  palette.accentHover,
  palette.accentDark,
  '#3D31A3',
  '#2F2680',
];

/** Аква: прогресс и успех. */
const aqua: MantineColorsTuple = [
  '#E8FBF9',
  '#CDF5F1',
  '#A1EDE5',
  '#72E3D8',
  '#48D6C9',
  palette.teal,
  palette.tealDeep,
  '#0C8479',
  '#0A665E',
  '#074A44',
];

const correct: MantineColorsTuple = [
  '#E7FAF2',
  '#C9F2E0',
  '#9AE6C6',
  '#66D8A9',
  '#38C991',
  palette.correct,
  '#0E9F6E',
  '#0B815A',
  '#086447',
  '#054833',
];

export interface ButtonRole {
  readonly mantineVariant: 'filled' | 'white' | 'subtle';
  readonly color: string;
  readonly className: string;
}

export const BUTTON_ROLES: Record<'primary' | 'secondary' | 'ghost', ButtonRole> = {
  primary: {
    mantineVariant: 'filled',
    color: 'brand',
    className:
      'bg-accent text-on-accent shadow-button hover:shadow-button-hover active:shadow-button-pressed disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none',
  },
  secondary: {
    mantineVariant: 'white',
    color: 'brand',
    className:
      'border border-line bg-surface text-heading shadow-soft hover:border-accent/50 hover:bg-surface-muted active:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-50',
  },
  ghost: {
    mantineVariant: 'subtle',
    color: 'brand',
    className: 'border border-line bg-surface/70 text-heading hover:border-accent hover:bg-surface active:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-40',
  },
};

export const mantineTheme = createTheme({
  primaryColor: 'brand',
  primaryShade: 5,
  colors: { brand, aqua, correct },
  white: palette.surface,
  black: palette.heading,
  fontFamily: fonts.body.join(', '),
  headings: {
    fontFamily: fonts.heading.join(', '),
    fontWeight: '800',
    sizes: {
      h1: { fontSize: '3.5rem', lineHeight: '1.05' },
      h2: { fontSize: '2.5rem', lineHeight: '1.1' },
      h3: { fontSize: '1.5rem', lineHeight: '1.3' },
    },
  },
  defaultRadius: 'lg',
  radius: {
    sm: '10px',
    md: radii.input,
    lg: radii.button,
    xl: radii.card,
  },
  shadows: {
    sm: shadows.button,
    md: shadows.soft,
    lg: shadows.softLg,
    xl: shadows.node,
  },
  spacing: {
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  components: {
    Button: {
      defaultProps: { radius: 'lg', size: 'lg', color: 'brand', autoContrast: true },
    },
    Card: {
      defaultProps: { radius: 'xl', shadow: 'md', padding: 'xl', withBorder: false },
    },
    Paper: {
      defaultProps: { radius: 'xl', shadow: 'md', withBorder: false },
    },
    TextInput: {
      defaultProps: { radius: 'md', size: 'lg' },
    },
    Textarea: {
      defaultProps: { radius: 'md', size: 'lg' },
    },
    Progress: {
      defaultProps: { color: 'aqua', radius: 'xl' },
    },
    Modal: {
      defaultProps: {
        radius: 'xl',
        centered: true,
        overlayProps: { backgroundOpacity: 0.2, blur: 6 },
      },
      styles: {
        content: {
          background: glass.background,
          backdropFilter: `blur(${glass.blur})`,
        },
        header: { background: 'transparent' },
      },
    },
  },
});
